/** Schedule / reschedule a campaign beneficiary's inspection.
 *
 * Lifted out of CampaignLeads so the session drawer can use it too.
 * Reported 2026-09-25: an operator watching her daily calendar clicks
 * the appointment, the beneficiary phones to say they can't make it,
 * and there is no way to move it from there — the only Reschedule in
 * the product lived in the campaign leads table, which is not where
 * she is when the phone rings.
 *
 * One component rather than two, because the reschedule path is not
 * trivial: the backend may have to CLOSE an already-live session and
 * open a fresh one, and the operator has to be told a new room was
 * created rather than assuming the old one moved. A second
 * implementation would have got that wrong.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { MuiDateTimePicker } from "@/components/MuiDateTimePicker";
import { SlotAvailability } from "@/components/SlotAvailability";
import { ApiPaths, api } from "@/lib/api";
import { describeApiError } from "@/lib/apiErrors";
import type { Lead, Paginated } from "@/lib/types";
import { useSlotGrid } from "@/lib/slotGrid";

export function ScheduleLeadModal({
  lead,
  campaignId,
  onClose,
}: {
  lead: Lead;
  campaignId: string;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  // Pre-fill from the existing open session when this is a reschedule.
  // The MUI picker speaks full ISO instants, so pass the server value
  // straight through.
  const isReschedule = !!lead.next_scheduled_for;
  const [whenIso, setWhenIso] = useState(lead.next_scheduled_for || "");
  const grid = useSlotGrid();
  const minDate = new Date().toISOString().slice(0, 10);
  const [notes, setNotes] = useState(lead.next_session_notes || "");
  // Set when the backend had to close the previous (already-live)
  // session and open a fresh one instead of moving it in place — the
  // operator should know a new room was created, not just assume the
  // old one's time changed (backlog 2026-09-18).
  const [recreatedNotice, setRecreatedNotice] = useState(false);

  // R4 of CONTACTS_REFACTOR — surface a warning if this contact
  // (matched by email or phone via the Contacts list) is enrolled in
  // OTHER active campaigns. High-value signal: operator can decide
  // to coordinate with the other campaign instead of double-booking.
  const otherMemberships = useQuery({
    queryKey: ["lead-other-memberships", lead.contact_email || lead.contact_phone, campaignId],
    queryFn: async () => {
      // Look up the contact by email/phone via the top-level search.
      const params: Record<string, string> = {};
      if (lead.contact_email) params.q = lead.contact_email;
      else if (lead.contact_phone) params.q = lead.contact_phone;
      else return [];
      const contactsRes = await api.get<Paginated<{ id: string }>>(
        ApiPaths.contacts,
        { params },
      );
      const matched = contactsRes.data.results?.[0];
      if (!matched) return [];
      const res = await api.get<
        { id: string; campaign: string; status: string }[]
      >(ApiPaths.contactMemberships(matched.id));
      return (Array.isArray(res.data) ? res.data : []).filter(
        (m) => m.campaign !== campaignId,
      );
    },
    enabled: !!(lead.contact_email || lead.contact_phone),
  });

  // The two refusals an operator can act on, in their own language
  // rather than the API's English `detail`.
  const scheduleErrorText = (err: unknown): string | null => {
    if (!axios.isAxiosError(err)) return null;
    const data = err.response?.data as
      | { reason?: string; slot_minutes?: number }
      | undefined;
    if (data?.reason === "slot_taken") return t("scheduling.slotTaken");
    if (data?.reason === "off_grid") {
      return t("scheduling.offGrid", { minutes: data.slot_minutes ?? 30 });
    }
    return null;
  };

  const submit = useMutation({
    mutationFn: async () => {
      const res = await api.post<{ recreated?: boolean }>(
        ApiPaths.campaignLeadSchedule(campaignId, lead.id),
        { scheduled_for: whenIso, notes: notes.trim() },
      );
      return res.data;
    },
    onSuccess: (data) => {
      void qc.invalidateQueries({ queryKey: ["campaign-leads", campaignId] });
      void qc.invalidateQueries({ queryKey: ["campaign", campaignId] });
      if (data?.recreated) {
        // The old session had already gone live — don't close silently,
        // the operator needs to know a fresh session/room was opened.
        setRecreatedNotice(true);
        return;
      }
      onClose();
    },
  });

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={t("leads.scheduleButton")}
      className="modal-backdrop bg-black/70"
    >
      {/* Wider than the default dialog: the slot grid is the point of
          this modal, and at max-w-md it wrapped into three cramped rows
          (reported 2026-09-27). */}
      <div className="modal-panel w-full max-w-2xl space-y-3">
        {recreatedNotice ? (
          <>
            <h2 className="text-lg font-semibold">
              {t("leads.rescheduleRecreatedTitle", "New session created")}
            </h2>
            <p className="rounded-md border border-nb-mint/40 bg-nb-mint/10 px-3 py-2 text-sm text-nb-text">
              {t(
                "leads.rescheduleRecreatedHint",
                "The previous session had already started, so it couldn't just be moved — it's been closed and a new one opened at the new time. The beneficiary got a fresh join link.",
              )}
            </p>
            <div className="flex justify-end">
              <button type="button" className="btn-primary text-sm" onClick={onClose}>
                {t("common.ok", "OK")}
              </button>
            </div>
          </>
        ) : (
          <>
        <h2 className="text-lg font-semibold">
          {isReschedule
            ? t("leads.rescheduleTitle", {
                name: `${lead.contact_first_name} ${lead.contact_last_name}`.trim(),
                defaultValue: "Reschedule {{name}}",
              })
            : t("leads.scheduleTitle", {
                name: `${lead.contact_first_name} ${lead.contact_last_name}`.trim(),
              })}
        </h2>
        {isReschedule && lead.contact_email && (
          <p className="rounded-md border border-nb-mint/40 bg-nb-mint/10 px-3 py-2 text-xs text-nb-text">
            {t(
              "leads.rescheduleEmailHint",
              "Changing the time will send the beneficiary a new email with the updated slot. Leaving the time as-is will not.",
            )}
          </p>
        )}
        {otherMemberships.data && otherMemberships.data.length > 0 && (
          <p className="rounded-md border border-cvp-warn/40 bg-cvp-warn/10 px-3 py-2 text-xs text-cvp-warn">
            {t("leads.scheduleCrossCampaignWarn", {
              count: otherMemberships.data.length,
            })}
          </p>
        )}
        {lead.contact_email && (
          <p className="text-xs text-nb-text-3">
            {t("leads.scheduleEmailHint", { email: lead.contact_email })}
          </p>
        )}
        {!lead.contact_email && (
          <p className="text-xs text-cvp-warn">
            {t("leads.scheduleNoEmailWarning")}
          </p>
        )}
        <label className="space-y-1 text-xs text-nb-text-2">
          <span>{t("leads.scheduleWhenLabel")}</span>
          <MuiDateTimePicker
            value={whenIso}
            onChange={setWhenIso}
            minDate={minDate}
            {...grid}
          />
        </label>
        {/* The campaign's own operators + its per-day booking cap, so
            what she sees here matches what the beneficiary would be
            offered for the same slot. */}
        <SlotAvailability
          value={whenIso}
          onPick={setWhenIso}
          campaignId={campaignId}
          // A move must not see its own appointment as the thing
          // occupying the slot it is already in.
          excludeSessionId={lead.next_session_id}
        />
        <label className="space-y-1 text-xs text-nb-text-2">
          <span>{t("leads.scheduleNotesLabel")}</span>
          <textarea
            className="input min-h-[4rem]"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
        </label>
        {submit.isError && (
          <p className="text-sm text-cvp-danger">
            {scheduleErrorText(submit.error) ??
              describeApiError(submit.error, t("leads.scheduleErrorGeneric"))}
          </p>
        )}
        <div className="flex justify-end gap-2">
          <button type="button" className="btn-ghost text-sm" onClick={onClose}>
            {t("common.cancel")}
          </button>
          <button
            type="button"
            className="btn-primary text-sm"
            disabled={!whenIso || submit.isPending}
            onClick={() => submit.mutate()}
          >
            {submit.isPending
              ? t("leads.scheduling")
              : isReschedule
                ? t("leads.rescheduleButton", "Reschedule")
                : t("leads.scheduleButton")}
          </button>
        </div>
          </>
        )}
      </div>
    </div>
  );
}
